import { Injectable } from '@angular/core';
import { Route, Router } from '@angular/router';
import { aquacultureChildren } from 'app/app-aquaculture.routes';
import { wildHarvestChildren } from 'app/app-wild-harvest.routes';

export interface SearchResult {
  id: string;
  title: string;
  subtitle?: string;
  value: string;
  link: string;
}

export interface SearchResultSet {
  id: string;
  label: string;
  results: SearchResult[];
}

type FlatSearchRoute = {
  pathSegment: string;
  link: string;
  title?: string;
  subtitle?: string;
  data?: any;
  redirectTo?: Route['redirectTo'];
};

@Injectable({ providedIn: 'root' })
export class AppSearchService {
  constructor(private _router: Router) {}

  search(query: string): SearchResultSet[] {
    const q = (query || '').trim().toLowerCase();
    if (!q) {
      return [];
    }

    const scope = this.getActiveScope();
    const routes = scope === 'aquaculture' ? aquacultureChildren : wildHarvestChildren;
    const basePath = `/${scope}`;

    const flattened = this.flattenRoutes(routes, basePath);

    const results = flattened
      .filter((route) => this.isSearchable(route))
      .filter((route) => {
        const title = (route.title ?? '').toLowerCase();
        const subtitle = (route.subtitle ?? '').toLowerCase();
        const link = route.link.toLowerCase();

        return title.includes(q) || subtitle.includes(q) || link.includes(q);
      })
      .map((route, index) => ({
        id: `${route.link}-${index}`,
        title: route.title || this.humanize(route.pathSegment),
        subtitle: route.subtitle || route.link,
        value: route.title || this.humanize(route.pathSegment),
        link: route.link,
      }));

    return results.length
      ? [
          {
            id: 'pages',
            label: 'Pages',
            results,
          },
        ]
      : [];
  }

  private getActiveScope(): 'aquaculture' | 'wild-harvest' {
    const url = this._router.url;

    if (url.startsWith('/wild-harvest')) {
      return 'wild-harvest';
    }

    return 'aquaculture';
  }

  private flattenRoutes(routes: Route[], parentPath = ''): FlatSearchRoute[] {
    const result: FlatSearchRoute[] = [];

    for (const route of routes) {
      const routePath = route.path ?? '';

      const currentPath = routePath && routePath !== '**' ? `${parentPath}/${routePath}`.replace(/\/+/g, '/') : parentPath;

      if (routePath && routePath !== '**') {
        result.push({
          pathSegment: routePath,
          link: currentPath,
          title: this.extractTitle(route),
          subtitle: route.data?.subtitle,
          data: route.data,
          redirectTo: route.redirectTo,
        });
      }

      if (route.children?.length) {
        result.push(...this.flattenRoutes(route.children, currentPath));
      }
    }

    return result;
  }

  private isSearchable(route: FlatSearchRoute): boolean {
    if (!route.link) {
      return false;
    }

    if (route.redirectTo) {
      return false;
    }

    if (route.pathSegment.includes(':')) {
      return false;
    }

    if (route.data?.hideInSearch === true) {
      return false;
    }

    return true;
  }

  private extractTitle(route: Route): string {
    if (typeof route.title === 'string') {
      return route.title;
    }

    if (route.data?.searchLabel) {
      return route.data.searchLabel;
    }

    if (route.data?.title) {
      return route.data.title;
    }

    return this.humanize(route.path ?? '');
  }

  private humanize(value: string): string {
    return (value || '')
      .split('/')
      .pop()!
      .replace(/-/g, ' ')
      .replace(/\b\w/g, (char) => char.toUpperCase());
  }
}
